import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { AlertTriangle, Plus, Trash2 } from "lucide-react";

/** Score → colour helper (extracted so it's testable in isolation). */
export function scoreBadgeClass(score) {
  if (score >= 15) return "bg-[#FEE2E2] text-[#B91C1C] border-[#FCA5A5]";
  if (score >= 8) return "bg-[#FEF3C7] text-[#92400E] border-[#FCD34D]";
  return "bg-[#D1FAE5] text-[#065F46] border-[#6EE7B7]";
}

const ScoreSelect = ({ value, onChange, testid, disabled }) => (
  <select disabled={disabled} data-testid={testid} value={value} onChange={e => onChange(Number(e.target.value))} className="w-full border border-gray-300 px-2 py-1 text-sm bg-white">
    {[1, 2, 3, 4, 5].map(n => <option key={n} value={n}>{n}</option>)}
  </select>
);

export default function HazardsTable({ hazards, updateHazard, removeHazard, addHazard, readOnly, unmitigatedHigh }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs uppercase tracking-wider font-semibold text-gray-700">Hazards</span>
        {!readOnly && <button type="button" onClick={addHazard} data-testid="add-hazard-btn" className="text-xs uppercase tracking-wider font-bold text-[#01ADEF] hover:text-[#0198D6]"><Plus className="w-3 h-3 inline mr-1" />Add Hazard</button>}
      </div>
      <div className="border border-gray-200">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-200 bg-gray-50 text-xs uppercase tracking-wider text-gray-600">
              <th className="text-left px-3 py-2 min-w-[200px]">Hazard</th>
              <th className="px-3 py-2 w-24">Likelihood</th>
              <th className="px-3 py-2 w-24">Severity</th>
              <th className="px-3 py-2 w-20">Score</th>
              <th className="text-left px-3 py-2 min-w-[220px]">Controls</th>
              <th className="px-3 py-2 w-24">Accepted</th>
              {!readOnly && <th className="px-3 py-2 w-8"></th>}
            </tr>
          </thead>
          <tbody data-testid="hazards-table">
            {hazards.length === 0 ? (
              <tr><td colSpan={7} className="text-center py-6 text-gray-500 text-sm">No hazards added.</td></tr>
            ) : hazards.map((h, i) => {
              const score = (h.likelihood || 0) * (h.severity || 0);
              return (
                <tr key={h._key || `hz-${i}`} className="border-b border-gray-100" data-testid={`hazard-row-${i}`}>
                  <td className="px-3 py-2"><Input disabled={readOnly} data-testid={`hazard-name-${i}`} value={h.hazard} onChange={e => updateHazard(i, { hazard: e.target.value })} className="rounded-md text-sm" /></td>
                  <td className="px-3 py-2"><ScoreSelect disabled={readOnly} testid={`hazard-likelihood-${i}`} value={h.likelihood} onChange={v => updateHazard(i, { likelihood: v })} /></td>
                  <td className="px-3 py-2"><ScoreSelect disabled={readOnly} testid={`hazard-severity-${i}`} value={h.severity} onChange={v => updateHazard(i, { severity: v })} /></td>
                  <td className="px-3 py-2"><span className={`inline-block text-xs font-bold px-2 py-1 border font-mono ${scoreBadgeClass(score)}`}>{score}</span></td>
                  <td className="px-3 py-2"><Input disabled={readOnly} data-testid={`hazard-controls-${i}`} value={h.controls || ""} onChange={e => updateHazard(i, { controls: e.target.value })} placeholder="Controls in place…" className="rounded-md text-sm" /></td>
                  <td className="px-3 py-2 text-center"><Checkbox disabled={readOnly} data-testid={`hazard-accepted-${i}`} checked={!!h.accepted} onCheckedChange={v => updateHazard(i, { accepted: !!v })} /></td>
                  {!readOnly && <td className="px-3 py-2"><button type="button" onClick={() => removeHazard(i)} className="text-gray-400 hover:text-[#DC2626]"><Trash2 className="w-3.5 h-3.5" /></button></td>}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {unmitigatedHigh.length > 0 && (
        <div className="mt-2 text-xs text-[#B91C1C] flex items-start gap-2 border-l-2 border-[#DC2626] pl-3 py-1" data-testid="unmitigated-warning">
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
          <span><b>{unmitigatedHigh.length} high-risk hazard(s)</b> not accepted with controls: {unmitigatedHigh.map(h => h.hazard).join(", ")}</span>
        </div>
      )}
    </div>
  );
}
